import React, { useState, useMemo, useEffect } from 'react';
import backupData from './names-clean.json';
import './App.css';
import Pagination from './components/Pagination';
import NameRow from './components/NameRow';
import Dropdown from './components/Dropdown';
import EmailModal from './components/EmailModal';
import { getPaginationNumbers, getGenderLabel, getOrigin, generateNameList } from './utils/utils';
import { ALPH_LANGS, ALPH_POSITIONS, ALPHABET, PAGE_SIZE, CATEGORIES } from './constants';
import TopNav from './components/TopNav';
import { AuthProvider } from './context/AuthContext';
import EditNameModal from './components/EditNameModal';
import { LuMailCheck, LuSparkles, LuSearch, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { TbFilterSearch, TbFilterOff, TbZoomReset } from 'react-icons/tb';
import CloseableMessage from './components/CloseableMessage';
import { useDebounce } from 'use-debounce';
import { useSearchParams } from 'react-router-dom';

const baseURL = process.env.NODE_ENV === 'development' ? 'https://curser-names.vercel.app/' : '/';
const FIRST_VISIT_MESSAGE_KEY = 'namesWelcomeMessageSeen';

function AppContent() {
  // State for search, filters, pagination, expanded rows, sorting, likes, and popup
  const [searchParams] = useSearchParams();
  // Pre-fill from a shared Name Card link (e.g. ?search=Aram) so the recipient
  // lands directly on the name that was shared with them.
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState([]);
  const [alphabet, setAlphabet] = useState('');
  const [alphabetLang, setAlphabetLang] = useState('eng_word');
  const [alphabetPos, setAlphabetPos] = useState('first');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [sortCol, setSortCol] = useState('eng_word');
  const [sortDir, setSortDir] = useState('asc');
  const [liked, setLiked] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('likedNames') || '[]');
    } catch {
      return [];
    }
  });
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(() => window.innerWidth > 600); // closed by default on mobile
  const [alphabetOpen, setAlphabetOpen] = useState(true); // alphabet grid is the tallest filter, collapsed by default
  const [namesData, setNamesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Responsive helpers
  const [isMobile, setIsMobile] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedName, setSelectedName] = useState(null);
  const [debouncedSearch] = useDebounce(search, 2000);
  const [similarNames, setSimilarNames] = useState([]);
  const [isLoadingSimilar, setIsLoadingSimilar] = useState(false);
  const [syriacFontType, setSyriacFontType] = useState('E');
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(() => {
    const hasSeen = localStorage.getItem(FIRST_VISIT_MESSAGE_KEY) === 'true';
    if (!hasSeen) {
      localStorage.setItem(FIRST_VISIT_MESSAGE_KEY, 'true');
    }
    return !hasSeen;
  });

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.trim()?.length > 3) {
      // and only if text is in English.
      if (!debouncedSearch.match(/^[a-zA-Z]+$/)) {
        return;
      }
      fetchSimilarNames(debouncedSearch);
    } else {
      setSimilarNames([]);
      setIsLoadingSimilar(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchNames = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${baseURL}api/database`);
      const json = await res.json();
      console.log('data: ', json);
      setNamesData(json);
    } catch (err) {
      setNamesData(backupData);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNames();
  }, []);

  // Persist liked names in localStorage
  useEffect(() => {
    localStorage.setItem('likedNames', JSON.stringify(liked));
  }, [liked]);

  // Filtering logic
  const filteredNames = useMemo(() => {
    let result = namesData.filter((n) => n.status === 'approved');
    // Search (across eng_word, arb_word, syr_word)
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      result = result.filter(
        (n) =>
          (n.eng_word && n.eng_word.toLowerCase().includes(s)) ||
          (n.arb_word && n.arb_word.includes(s)) ||
          (n.syr_word && n.syr_word.includes(s)) ||
          (Array.isArray(n.variations) &&
            n.variations?.length > 0 &&
            n.variations?.some((v) => v.toLowerCase().includes(s)))
      );
    }
    // Gender
    if (gender) {
      if (gender === 'neutral') {
        result = result.filter((n) => n.gender === '2' || n.gender === 'neutral');
      } else {
        result = result.filter((n) => n.gender === gender);
      }
    }
    // Category (must match all selected)
    if (category.length > 0) {
      result = result.filter((n) =>
        Array.isArray(n.category) ? category.every((cat) => n.category.includes(cat)) : false
      );
    }
    // Alphabet
    if (alphabet) {
      result = result.filter((n) => {
        const word = n[alphabetLang];
        if (!word || word.length === 0) return false;
        const letter =
          alphabetPos === 'first' ? word[0].toUpperCase() : word[word.length - 1].toUpperCase();
        return letter === alphabet;
      });
    }
    return result;
  }, [search, gender, category, alphabet, alphabetLang, alphabetPos, namesData]);

  // Sorting logic
  const sortedNames = useMemo(() => {
    const arr = [...filteredNames];
    arr.sort((a, b) => {
      let aVal, bVal;
      if (sortCol === 'eng_word') {
        aVal = a.eng_word || '';
        bVal = b.eng_word || '';
      } else if (sortCol === 'gender') {
        aVal = getGenderLabel(a.gender);
        bVal = getGenderLabel(b.gender);
      } else if (sortCol === 'category') {
        aVal = Array.isArray(a.category) ? a.category[0] || '' : '';
        bVal = Array.isArray(b.category) ? b.category[0] || '' : '';
      } else if (sortCol === 'origin') {
        aVal = getOrigin(a);
        bVal = getOrigin(b);
      } else {
        aVal = a[sortCol] || '';
        bVal = b[sortCol] || '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredNames, sortCol, sortDir]);

  // Pagination logic
  const totalPages = Math.ceil(sortedNames.length / PAGE_SIZE) || 1;
  const paginatedNames = sortedNames.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Handlers for filters, search, reset, expand, pagination, sorting, likes, popup
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const fetchSimilarNames = async (name) => {
    setIsLoadingSimilar(true);
    try {
      const res = await fetch(`${baseURL}api/names/similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      const { related } = data;

      if (related && related.length > 0) {
        setSimilarNames(related.filter((n) => n.score > 0.7));
      } else {
        setSimilarNames([]);
      }
    } catch (err) {
      console.error('Error fetching similar names:', err);
      setSimilarNames([]);
    } finally {
      setIsLoadingSimilar(false);
    }
  };

  const handleGender = (g) => {
    setGender(gender === g ? '' : g);
    setPage(1);
  };
  const handleCategory = (cat) => {
    setCategory((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
    setPage(1);
  };
  const handleAlphabet = (a) => {
    setAlphabet(alphabet === a ? '' : a);
    setPage(1);
  };
  const handleAlphabetLang = (value) => {
    setAlphabetLang(value);
    setAlphabet('');
    setPage(1);
  };
  const handleAlphabetPos = (value) => {
    setAlphabetPos(value);
    setAlphabet('');
    setPage(1);
  };
  const handleReset = () => {
    setSearch('');
    setGender('');
    setCategory([]);
    setAlphabet('');
    setAlphabetLang('eng_word');
    setAlphabetPos('first');
    setPage(1);
    setExpanded(null);
    setSortCol('eng_word');
    setSortDir('asc');
  };
  const handleExpand = (id) => setExpanded(expanded === id ? null : id);
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };
  const handleLike = (id) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const openEmailPopup = () => {
    setShowEmailPopup(true);
    setEmailInput('');
    setEmailError('');
    setEmailSent(false);
  };
  const closeEmailPopup = () => {
    setShowEmailPopup(false);
    setEmailInput('');
    setEmailError('');
    setEmailSent(false);
  };
  const handleEmailInput = (e) => {
    setEmailInput(e.target.value);
    setEmailError('');
  };
  const handleSendEmail = async (e) => {
    e.preventDefault();
    // Simple email /validation/
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailInput)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    // Simulate sending email
    const likedNamesToSend = namesData.filter((n) => liked.includes(n._id));

    const emailData = {
      content: generateNameList(likedNamesToSend),
      to: emailInput,
    };

    try {
      const res = await fetch(`${baseURL}api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      });

      const data = await res.json();
      if (res.ok) {
        console.log('email sent.');
        setEmailSent(true);
      } else {
        setEmailSent(false);
        alert('❌ ' + data.message);
      }
    } catch (err) {
      console.error(err);
      setEmailSent(false);
      alert('❌ Something went wrong');
    }
    closeEmailPopup();
  };

  const handleRemoveLiked = (id) => {
    setLiked((prev) => prev.filter((nameId) => nameId !== id));
  };

  // Close modal if no liked names are left
  useEffect(() => {
    if (showEmailPopup && liked.length === 0) {
      closeEmailPopup();
    }
  }, [liked, showEmailPopup]);

  // For highlighting active filters
  const isGenderActive = (g) =>
    (g === 'neutral' && (gender === '2' || gender === 'neutral')) || gender === g;
  const isAlphabetActive = (a) => alphabet === a;

  // Alphabet letters for the selected language
  const getAlphabetLetters = () => {
    if (alphabetLang === 'eng_word') return ALPHABET.en;
    if (alphabetLang === 'arb_word') return ALPHABET.ar;
    if (alphabetLang === 'syr_word') return ALPHABET.sy;
    return [];
  };

  // For showing sort arrow
  const getSortArrow = (col) => {
    if (sortCol !== col) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  // Liked names data
  const likedNames = useMemo(
    () => namesData.filter((n) => liked.includes(n._id)),
    [liked, namesData]
  );

  // Count of active filters, surfaced on the mobile toggle so it's visible while collapsed
  const activeFilterCount = (gender ? 1 : 0) + category.length + (alphabet ? 1 : 0);

  return (
    <div className="app-container">
      {/* Main Header with Logo */}
      <TopNav syriacFontType={syriacFontType} setSyriacFontType={setSyriacFontType} />

      <div className="app-content">
        {/* Mobile filter toggle button */}
        {isMobile && (
          <button
            className="filters-toggle-btn"
            onClick={() => setFiltersOpen((v) => !v)}
            style={{
              margin: '1rem auto',
              display: 'block',
              background: '#7c3aed',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '0.5rem 1.2rem',
              fontWeight: 600,
            }}
          >
            {filtersOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TbFilterOff size={20} />
                Hide Filters
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TbFilterSearch size={20} />
                Show Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </div>
            )}
          </button>
        )}
        {/* Filters and Search */}
        {(!isMobile || filtersOpen) && (
          <aside className="filters">
            {/* Gender Filter */}
            <div className="filter-group">
              <div>Gender</div>
              <button
                className={isGenderActive('male') ? 'active' : ''}
                onClick={() => handleGender('male')}
              >
                Boy
              </button>
              <button
                className={isGenderActive('female') ? 'active' : ''}
                onClick={() => handleGender('female')}
              >
                Girl
              </button>
              <button
                className={isGenderActive('neutral') ? 'active' : ''}
                onClick={() => handleGender('neutral')}
              >
                Neutral
              </button>
            </div>
            {/* Category Filter */}
            <div className="filter-group">
              <div>Category</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {CATEGORIES.map((cat) => (
                  <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={category.includes(cat)}
                      onChange={() => handleCategory(cat)}
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>
            {/* Alphabet Filter - collapsed by default: tallest filter, least used */}
            <div className="filter-group">
              <button
                type="button"
                className="alphabet-toggle"
                onClick={() => setAlphabetOpen((v) => !v)}
                aria-expanded={alphabetOpen}
              >
                Alphabet{alphabet ? ` (${alphabet})` : ''}
                {alphabetOpen ? <LuChevronUp size={18} /> : <LuChevronDown size={18} />}
              </button>
              {alphabetOpen && (
                <div className="alphabet-panel">
                  <div className="alphabet-dropdown-row">
                    <Dropdown
                      options={ALPH_LANGS}
                      value={alphabetLang}
                      onChange={handleAlphabetLang}
                      ariaLabel="Alphabet language"
                    />
                    <Dropdown
                      options={ALPH_POSITIONS}
                      value={alphabetPos}
                      onChange={handleAlphabetPos}
                      ariaLabel="Alphabet letter position"
                    />
                  </div>
                  {getAlphabetLetters().map((letter) => (
                    <button
                      key={letter}
                      className={isAlphabetActive(letter) ? 'active' : ''}
                      onClick={() => handleAlphabet(letter)}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reset + Favorites actions, kept together but visually distinct from filter groups above */}
            <div className="filter-actions">
              <button className="reset-btn" onClick={handleReset}>
                <TbZoomReset size={20} />
                Reset Filters
              </button>

              {/* Favorites section - separated since it acts on liked names, not filters */}
              {liked.length > 0 && (
                <div className="favorites-section">
                  <div className="filter-group-title">Your Favorites</div>
                  <button className="email-names-btn" onClick={openEmailPopup}>
                    <LuMailCheck size={20} />
                    Email Liked Names ({liked.length})
                  </button>
                </div>
              )}
            </div>
          </aside>
        )}

        <main className="main-content">
          {!isMobile && showWelcomeMessage && (
            <CloseableMessage onClose={() => setShowWelcomeMessage(false)}>
              🙋🏽‍♂️Let's help you find the perfect name for your baby 🧷👶. All these names are from
              Assyrian Syriac Chaldean tradition and language. Sourced from differnet books and our
              network
            </CloseableMessage>
          )}
          {/* Search and results in a shared container for width control */}
          <div className="results-container">
            {/* Search Bar */}
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search baby names..."
                value={search}
                onChange={handleSearch}
              />
              <LuSearch className="search-icon" />
            </div>

            {/* Similar Names Section */}
            {search.trim() && (
              <>
                {isLoadingSimilar ? (
                  <div className="similar-names-container">
                    <div className="similar-names-loading">
                      <LuSparkles className="ai-loading-icon" />
                      <span>Finding similar names...</span>
                    </div>
                  </div>
                ) : similarNames.length > 0 ? (
                  <div className="similar-names-container">
                    <div className="similar-names-content">
                      <span className="similar-names-title">Similar names:</span>
                      <div className="similar-names-list">
                        {similarNames.map((item, index) => {
                          const nameText = item.eng_word || 'Unknown';
                          return (
                            <span key={index}>
                              <span
                                className="similar-name-item"
                                onClick={() => {
                                  handleReset();
                                  setSearch(nameText);
                                }}
                              >
                                {nameText}
                              </span>
                              {index < similarNames.length - 1 && ', '}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {/* Name List (paginated, expandable) */}
            <div className="name-list">
              {isLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Loading names...</div>
                </div>
              ) : paginatedNames.length === 0 ? (
                <div className="no-results">
                  No names found. Please try different search or filters.{' '}
                  <span onClick={handleReset} style={{ color: '#7c3aed', cursor: 'pointer' }}>
                    Reset Filters
                  </span>
                </div>
              ) : (
                <table className="names-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('eng_word')}>
                        Name{getSortArrow('eng_word')}
                      </th>
                      <th className="sortable" onClick={() => handleSort('gender')}>
                        Gender{getSortArrow('gender')}
                      </th>
                      {!isMobile && (
                        <th className="sortable" onClick={() => handleSort('category')}>
                          Category{getSortArrow('category')}
                        </th>
                      )}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedNames.map((n) => (
                      <NameRow
                        key={n._id}
                        name={n}
                        isMobile={isMobile}
                        expanded={expanded}
                        handleExpand={handleExpand}
                        getGenderLabel={getGenderLabel}
                        liked={liked}
                        handleLike={handleLike}
                        setIsEditModalOpen={setIsEditModalOpen}
                        setSelectedName={setSelectedName}
                        syriacFontType={syriacFontType}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              isMobile={isMobile}
              handlePageChange={handlePageChange}
              getPaginationNumbers={getPaginationNumbers}
            />
          </div>
        </main>
      </div>

      {/* Email Popup */}
      {showEmailPopup && (
        <EmailModal
          closeEmailPopup={closeEmailPopup}
          emailSent={emailSent}
          handleSendEmail={handleSendEmail}
          emailInput={emailInput}
          handleEmailInput={handleEmailInput}
          likedNames={likedNames}
          emailError={emailError}
          handleRemoveLiked={handleRemoveLiked}
          setLiked={setLiked}
        />
      )}
      <EditNameModal
        isOpen={selectedName && isEditModalOpen}
        onClose={() => {
          setSelectedName(null);
          setIsEditModalOpen(false);
        }}
        origName={selectedName}
        onUpdate={fetchNames}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
